import { IBulkPdfUploadJob } from './bulk-pdf-upload-job.entity';

interface NormalizedProjectSnapshotPayload {
  projectId?: string;
  entities: {
    projects?: Record<
      string,
      { audit?: { version?: number | null } | null } | null
    >;
    projectDetails?: Record<
      string,
      {
        bulkPdfUploadJobs?: string[] | null;
        permissionReport?: {
          project?: Record<string, boolean> | null;
        } | null;
      } | null
    >;
    bulkPdfUploadJobs?: Record<string, IBulkPdfUploadJob>;
  };
}

export interface BulkPdfProjectSnapshot {
  projectId: string;
  projectVersion: number;
  authorized: boolean;
  jobIds: string[];
  jobs: Record<string, IBulkPdfUploadJob>;
}

export function readBulkPdfProjectId(
  payload: NormalizedProjectSnapshotPayload
): string | null {
  const projectDetails = payload.entities.projectDetails;
  return (
    payload.projectId ??
    (projectDetails && Object.keys(projectDetails).length === 1
      ? Object.keys(projectDetails)[0]
      : null)
  );
}

/**
 * A Project aggregate's audit version is the ordering token shared by HTTP and SignalR payloads.
 * The per-job revision orders updates within that aggregate snapshot, but cannot prove whether an
 * omitted id was pruned or merely came from an older whole-Project response.
 */
export function readBulkPdfProjectSnapshot(
  payload: NormalizedProjectSnapshotPayload
): BulkPdfProjectSnapshot | null {
  const projectDetails = payload.entities.projectDetails;
  const projectId = readBulkPdfProjectId(payload);
  if (!projectId) {
    return null;
  }

  const details = projectDetails?.[projectId];
  const projectVersion = payload.entities.projects?.[projectId]?.audit?.version;
  if (!details || !Number.isFinite(projectVersion)) {
    return null;
  }

  const authorized =
    details.permissionReport?.project?.['bulkPdfUpload'] === true;
  return {
    projectId,
    projectVersion: projectVersion as number,
    authorized,
    // The server's permission filter deliberately sends an empty collection when access is
    // denied. Do not allow an inconsistent normalized payload to retain ids in that case.
    jobIds: authorized ? [...(details.bulkPdfUploadJobs ?? [])] : [],
    jobs: authorized ? (payload.entities.bulkPdfUploadJobs ?? {}) : {},
  };
}
